"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getTenantOrDefault } from "@/utils/getTenant";

export interface DemoColors {
  primary: string;
  secondary: string;
  tertiary: string;
  header: string;
  buttonText: string;
}

const DEFAULTS: DemoColors = {
  primary: "#292929",
  secondary: "#ffff00",
  tertiary: "#6C7C93",
  header: "#ffffff",
  buttonText: "#1D2530",
};

interface DemoThemeCtx {
  colors: DemoColors;
  setColor: (key: keyof DemoColors, value: string) => void;
  reset: () => void;
  logoUrl: string | null;
  setLogoUrl: (url: string | null) => void;
  headerLogoUrl: string | null;
  footer: FooterData | null;
  floatingLinks: FloatingLink[];
  externalLinks: ExternalAppLinks | null;
  sac: string | null;
  // Operadora do tenant atual (whitelabel.operator_id no Strapi). null quando
  // ainda não carregou ou quando o tenant não tem o campo preenchido.
  operatorId: number | null;
  themeReady: boolean;
  partnerFound: boolean;
}

const Ctx = createContext<DemoThemeCtx>({
  colors: DEFAULTS,
  setColor: () => {},
  reset: () => {},
  logoUrl: null,
  setLogoUrl: () => {},
  headerLogoUrl: null,
  footer: null,
  floatingLinks: [],
  externalLinks: null,
  sac: null,
  operatorId: null,
  themeReady: false,
  partnerFound: false,
});

// Black or white, whichever contrasts better against the given hex color.
function contrastText(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#FFFFFF";
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#1D2530" : "#FFFFFF";
}

function injectVars(colors: DemoColors) {
  const root = document.documentElement;
  root.style.setProperty("--demo-primary", colors.primary);
  root.style.setProperty("--demo-secondary", colors.secondary);
  root.style.setProperty("--demo-tertiary", colors.tertiary);
  root.style.setProperty("--demo-header", colors.header);
  root.style.setProperty("--demo-button-text", colors.buttonText);
  // Always-readable text for filled primary buttons — auto-contrasted against
  // the primary color. Demo mode never sets buttonText, so a black primary
  // would otherwise render black-on-black.
  root.style.setProperty(
    "--demo-button-text-contrast",
    contrastText(colors.primary)
  );
}

const LS_COLORS_KEY = "demo-theme.colors";
const LS_LOGO_KEY = "demo-theme.logo";

function loadColors(): DemoColors {
  try {
    const raw = localStorage.getItem(LS_COLORS_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {}
  return DEFAULTS;
}

function loadLogo(): string | null {
  try {
    return localStorage.getItem(LS_LOGO_KEY);
  } catch {}
  return null;
}


interface StrapiEmbarcaLogResponse {
  data: Array<{
    id: number;
    landing_page?: {
      hero?: {
        logo?: { url?: string };
      };
    };
    product?: {
      logo?: { url?: string };
      colors?: {
        primary_color?: string;
        secondary_color?: string;
        tertiary_color?: string;
        button_color?: string;
        button_text_color?: string;
      };
    };
    pages?: Array<{ __component: string; sac?: string }>;
  }>;
}

export interface FooterSubtopic {
  id: number;
  order: number;
  content: string;
  link: string;
  new_tab: boolean;
  is_telephone: boolean;
  is_email: boolean;
}

export interface FooterTopic {
  id: number;
  order: number;
  title: string;
  subtopic: FooterSubtopic[];
}

export interface FooterData {
  description: string;
  logo: { url: string } | null;
  background_color: string | null;
  text_color: string | null;
  topic: FooterTopic[];
  social_media: Array<{ id: number; link: string; image: { url: string } }>;
}

export interface FloatingLink {
  id: number;
  link: string;
  content_tooltip: string;
  icon: { url: string };
  icon_responsive: { url: string };
}

export interface ExternalAppLinks {
  app_store: string | null;
  play_store: string | null;
}

interface StrapiWhitelabelResponse {
  data: Array<{
    id: number;
    operator_id?: string | number | null;
    header?: {
      logo?: { url?: string };
    };
    footer?: {
      description?: string;
      logo?: { url?: string };
      background_color?: string | null;
      text_color?: string | null;
      topic?: FooterTopic[];
      social_media?: Array<{ id: number; link: string; image: { url: string } }>;
    };
    floating_link?: FloatingLink[];
    external_link?: {
      app?: { app_store?: string; play_store?: string };
    };
  }>;
}

const FAILED = { colors: DEFAULTS, logoUrl: null, headerLogoUrl: null, footer: null, floatingLinks: [], externalLinks: null, sac: null, operatorId: null, found: false };

// `operator_id` é string no Strapi ("1", "12"). Converte para número e descarta
// vazio/inválido — sem fallback: pedido sem operadora resolvida não é criado.
function parseOperatorId(raw: string | number | null | undefined): number | null {
  if (raw == null || raw === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

// Só o operator_id da whitelabel do tenant. Usado no modo demo (onde o tema vem
// do localStorage e fetchPartnerTheme não roda) e como resolução sob demanda
// quando o contexto ainda não terminou de carregar.
export async function fetchOperatorId(
  subdomain: string
): Promise<number | null> {
  const base = process.env.NEXT_PUBLIC_API_STRAPI?.replace(/\/$/, "");
  if (!base) return null;
  try {
    const params = new URLSearchParams();
    params.append("filters[subdomain][$eq]", subdomain);
    params.append("fields[0]", "operator_id");
    const res = await fetch(`${base}/api/whitelabels?${params.toString()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const json: StrapiWhitelabelResponse = await res.json();
    return parseOperatorId(json?.data?.[0]?.operator_id);
  } catch {
    return null;
  }
}

async function fetchPartnerTheme(subdomain: string): Promise<{
  colors: DemoColors;
  logoUrl: string | null;
  headerLogoUrl: string | null;
  footer: FooterData | null;
  floatingLinks: FloatingLink[];
  externalLinks: ExternalAppLinks | null;
  sac: string | null;
  operatorId: number | null;
  found: boolean;
}> {
  const base = process.env.NEXT_PUBLIC_API_STRAPI?.replace(/\/$/, "");
  if (!base) return FAILED;

  try {
    // Loading screen logo + colors from embarca-log collection
    const elParams = new URLSearchParams();
    elParams.append("filters[whitelabel_subdomain][$eq]", subdomain);
    elParams.append("populate[product][populate][logo]", "true");
    elParams.append("populate[product][populate][colors]", "true");
    elParams.append("populate[pages]", "true");
    const elRes = await fetch(`${base}/api/embarca-logs?${elParams.toString()}`, { cache: "no-store" });
    if (!elRes.ok) return FAILED;
    const elJson: StrapiEmbarcaLogResponse = await elRes.json();
    const entry = elJson?.data?.[0];
    if (!entry) return FAILED;

    const faqPage = entry.pages?.find(
      (p) => p.__component === "embarca-log.page-faq"
    );
    const sac = faqPage?.sac || null;

    const c = entry.product?.colors;
    const colors: DemoColors = {
      primary: c?.primary_color || DEFAULTS.primary,
      secondary: c?.secondary_color || DEFAULTS.secondary,
      tertiary: c?.tertiary_color || DEFAULTS.tertiary,
      header: c?.button_color || DEFAULTS.header,
      buttonText: c?.button_text_color || DEFAULTS.buttonText,
    };
    const logoUrl = entry.product?.logo?.url || null;

    // Header logo + footer + floating links + external links from whitelabel collection
    const wlParams = new URLSearchParams();
    wlParams.append("filters[subdomain][$eq]", subdomain);
    wlParams.append("populate[header][populate][logo]", "true");
    wlParams.append("populate[footer][populate][logo]", "true");
    wlParams.append("populate[footer][populate][social_media][populate]", "*");
    wlParams.append("populate[footer][populate][topic][populate]", "*");
    wlParams.append("populate[floating_link][populate]", "*");
    wlParams.append("populate[external_link][populate]", "*");
    const wlRes = await fetch(`${base}/api/whitelabels?${wlParams.toString()}`, { cache: "no-store" });
    const wlJson: StrapiWhitelabelResponse = wlRes.ok ? await wlRes.json() : { data: [] };
    const wlEntry = wlJson?.data?.[0];
    const headerLogoUrl = wlEntry?.header?.logo?.url || null;

    const rawFooter = wlEntry?.footer;
    const footer: FooterData | null = rawFooter ? {
      description: rawFooter.description || "",
      logo: rawFooter.logo ? { url: rawFooter.logo.url! } : null,
      background_color: rawFooter.background_color ?? null,
      text_color: rawFooter.text_color ?? null,
      topic: rawFooter.topic || [],
      social_media: rawFooter.social_media || [],
    } : null;

    const floatingLinks: FloatingLink[] = wlEntry?.floating_link || [];
    const externalLinks: ExternalAppLinks | null = wlEntry?.external_link?.app ? {
      app_store: wlEntry.external_link.app.app_store || null,
      play_store: wlEntry.external_link.app.play_store || null,
    } : null;

    // operator_id vem da mesma entrada de whitelabel já buscada acima (filtrada
    // pelo tenant) — nenhum request extra. É o id usado no POST /orders.
    const operatorId = parseOperatorId(wlEntry?.operator_id);

    return { colors, logoUrl, headerLogoUrl, footer, floatingLinks, externalLinks, sac, operatorId, found: true };
  } catch {
    return FAILED;
  }
}

export function DemoThemeProvider({ children }: { children: React.ReactNode }) {
  const isDemo = process.env.NEXT_PUBLIC_DEMO === "true";
  const [colors, setColors] = useState<DemoColors>(DEFAULTS);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [headerLogoUrl, setHeaderLogoUrl] = useState<string | null>(null);
  const [footer, setFooter] = useState<FooterData | null>(null);
  const [floatingLinks, setFloatingLinks] = useState<FloatingLink[]>([]);
  const [externalLinks, setExternalLinks] = useState<ExternalAppLinks | null>(null);
  const [sac, setSac] = useState<string | null>(null);
  const [operatorId, setOperatorId] = useState<number | null>(null);
  const [themeReady, setThemeReady] = useState(isDemo);
  const [partnerFound, setPartnerFound] = useState(false);
  const initialized = useRef(false);

  // Demo mode: load from localStorage and keep picker in sync
  useEffect(() => {
    if (!isDemo) return;
    const saved = loadColors();
    const savedLogo = loadLogo();
    setColors(saved);
    setLogoUrl(savedLogo);
    // O tema vem do localStorage no demo, mas o operator_id continua sendo
    // necessário para criar pedido — busca só esse campo no Strapi.
    fetchOperatorId(getTenantOrDefault()).then(setOperatorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Non-demo mode: fetch partner theme from Strapi once on mount
  useEffect(() => {
    if (isDemo) return;
    // Tenant: header `Tenant` (SSR) → cookie → ?whitelabel → "embarca-ai".
    fetchPartnerTheme(getTenantOrDefault()).then(
      ({ colors: c, logoUrl: l, headerLogoUrl: h, footer: f, floatingLinks: fl, externalLinks: el, sac: s, operatorId: oid, found }) => {
        setColors(c);
        setLogoUrl(l);
        setHeaderLogoUrl(h);
        setFooter(f);
        setFloatingLinks(fl);
        setExternalLinks(el);
        setSac(s);
        setOperatorId(oid);
        setPartnerFound(found);
        injectVars(c);
        setThemeReady(true);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Demo mode: inject CSS vars and persist colors whenever they change
  useEffect(() => {
    if (!isDemo) return;
    injectVars(colors);
    if (initialized.current) {
      try {
        localStorage.setItem(LS_COLORS_KEY, JSON.stringify(colors));
      } catch {}
    }
    initialized.current = true;
  }, [colors, isDemo]);

  // Demo mode: persist logo whenever it changes (after mount)
  const isFirstLogo = useRef(true);
  useEffect(() => {
    if (!isDemo) return;
    if (isFirstLogo.current) {
      isFirstLogo.current = false;
      return;
    }
    try {
      if (logoUrl) localStorage.setItem(LS_LOGO_KEY, logoUrl);
      else localStorage.removeItem(LS_LOGO_KEY);
    } catch {}
  }, [logoUrl, isDemo]);

  const setColor = useCallback((key: keyof DemoColors, value: string) => {
    setColors((prev) => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => {
    setColors(DEFAULTS);
    setLogoUrl(null);
    try {
      localStorage.removeItem(LS_COLORS_KEY);
      localStorage.removeItem(LS_LOGO_KEY);
    } catch {}
  }, []);

  return (
    <Ctx.Provider
      value={{
        colors,
        setColor,
        reset,
        logoUrl,
        setLogoUrl,
        headerLogoUrl,
        footer,
        floatingLinks,
        externalLinks,
        sac,
        operatorId,
        themeReady,
        partnerFound,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useDemoTheme() {
  return useContext(Ctx);
}

// Returns live colors from context (sourced from demo picker or Strapi).
export function useThemeColors() {
  const { colors } = useContext(Ctx);
  return {
    primaryColor: colors.primary,
    secondaryColor: colors.secondary,
    tertiaryColor: colors.tertiary,
    headerColor: colors.header,
  };
}
